/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useState } from 'react';
import type { ResumeJSON } from '../types/resume';

// In-flight retailor lives here (not in BuilderWizard local state) so the
// background toast can keep the user informed across route changes.
type RetailorStatus = 'idle' | 'running' | 'done' | 'error';

export interface RetailorJobState {
  status: RetailorStatus;
  startedAt: number | null;
  finishedAt: number | null;
  result: ResumeJSON | null;
  error: string | null;
  // True once the user dismissed the in-page progress modal while the
  // retailor is still running. The cross-route toast then shows even on
  // the wizard route, since the modal is no longer covering progress.
  inBackground: boolean;
}

export interface RetailorJobContextValue extends RetailorJobState {
  start: () => void;
  complete: (r: ResumeJSON) => void;
  fail: (msg: string) => void;
  acknowledge: () => void;
  sendToBackground: () => void;
}

const Ctx = createContext<RetailorJobContextValue | null>(null);

const INITIAL: RetailorJobState = {
  status: 'idle',
  startedAt: null,
  finishedAt: null,
  result: null,
  error: null,
  inBackground: false,
};

export function RetailorJobProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<RetailorJobState>(INITIAL);

  const start = useCallback(() => {
    setState({ ...INITIAL, status: 'running', startedAt: Date.now() });
  }, []);
  const complete = useCallback((r: ResumeJSON) => {
    setState(s => ({ ...s, status: 'done', finishedAt: Date.now(), result: r, error: null }));
  }, []);
  const fail = useCallback((msg: string) => {
    setState(s => ({ ...s, status: 'error', finishedAt: Date.now(), error: msg }));
  }, []);
  const acknowledge = useCallback(() => setState(INITIAL), []);
  const sendToBackground = useCallback(() => {
    setState(s => ({ ...s, inBackground: true }));
  }, []);

  return (
    <Ctx.Provider value={{ ...state, start, complete, fail, acknowledge, sendToBackground }}>
      {children}
    </Ctx.Provider>
  );
}

export function useRetailorJob(): RetailorJobContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useRetailorJob must be used within RetailorJobProvider');
  return v;
}
